/**
 * Wann der Agent wirklich sendet: das Arbeitsfenster des LinkedIn-Kontos (`workDays`/`workHours`) ist die harte Grenze,
 * die Auswahl der Kampagne (`workTime`) schränkt es weiter ein — ein Schritt läuft nur, wenn BEIDE offen sind
 * (Backend: `campaign-work-window.ts`). Rein und testbar.
 */

import type { WizardAccount } from './campaign-wizard.types';

const WEEKDAYS = ['monday', 'tuesday', 'wednesday', 'thursday', 'friday'];
const DAY_ORDER = ['monday', 'tuesday', 'wednesday', 'thursday', 'friday', 'saturday', 'sunday'];

/** Fenster der Kampagnenauswahl, volle Stunden inklusive (wie `CAMPAIGN_WINDOWS` im Backend). */
export const CAMPAIGN_WINDOWS = [
  { days: WEEKDAYS, fromHour: 8, toHour: 17 },
  { days: WEEKDAYS, fromHour: 9, toHour: 16 },
  { days: [...WEEKDAYS, 'saturday'], fromHour: 8, toHour: 19 },
] as const;

export interface WorkWindow {
  /** Tage in Wochenreihenfolge (`monday` …). */
  days: string[];
  /** Zusammenhängende Stundenblöcke: [erste Stunde, Ende] – `[8, 11]` heißt 8–11 Uhr. */
  ranges: Array<[number, number]>;
}

/** Auswahl 3 = „Arbeitszeiten des LinkedIn-Kontos“: genau das Kontofenster, ohne weitere Einschränkung durch die Kampagne. */
export const WORK_TIME_ACCOUNT = 3;

/** Schnittmenge aus Kontofenster und Kampagnenauswahl (bei Auswahl 3 das reine Kontofenster); `null`, wenn das Konto kein Fenster hat (dann gilt nur die Auswahl). */
export function effectiveWorkWindow(
  account: Pick<WizardAccount, 'workDays' | 'workHours'> | null | undefined,
  workTime: number
): WorkWindow | null {
  const campaign =
    workTime === WORK_TIME_ACCOUNT ? null : (CAMPAIGN_WINDOWS[workTime] ?? CAMPAIGN_WINDOWS[0]);
  const accountDays = account?.workDays ?? [];
  const accountHours = (account?.workHours ?? []).filter(h => Number.isInteger(h) && h >= 0 && h <= 23);
  if (accountDays.length === 0 || accountHours.length === 0) return null;
  const days = DAY_ORDER.filter(
    d => accountDays.includes(d) && (!campaign || (campaign.days as readonly string[]).includes(d))
  );
  const hours = [...new Set(accountHours)]
    .filter(h => !campaign || (h >= campaign.fromHour && h <= campaign.toHour))
    .sort((a, b) => a - b);
  const ranges: Array<[number, number]> = [];
  for (const h of hours) {
    const last = ranges[ranges.length - 1];
    if (last && last[1] === h) last[1] = h + 1;
    else ranges.push([h, h + 1]);
  }
  return { days, ranges };
}

type Translate = (key: string, options?: Record<string, unknown>) => string;

const DAY_KEYS: Record<string, string> = {
  monday: 'wizRevDayMo',
  tuesday: 'wizRevDayTu',
  wednesday: 'wizRevDayWe',
  thursday: 'wizRevDayTh',
  friday: 'wizRevDayFr',
  saturday: 'wizRevDaySa',
  sunday: 'wizRevDaySu',
};

/** „Mo–Fr, 8–11 und 14–16 Uhr“; aufeinanderfolgende Tage ab drei werden zu „Mo–Fr“. Leeres Fenster: eigener Hinweis. */
export function workWindowLabel(window: WorkWindow, t: Translate): string {
  if (window.days.length === 0 || window.ranges.length === 0) return t('wizRevWorkWindowNone');
  const runs: string[][] = [];
  for (const day of window.days) {
    const last = runs[runs.length - 1];
    if (last && DAY_ORDER.indexOf(last[last.length - 1]) === DAY_ORDER.indexOf(day) - 1) last.push(day);
    else runs.push([day]);
  }
  const days = runs
    .map(run =>
      run.length >= 3 ? `${t(DAY_KEYS[run[0]])}–${t(DAY_KEYS[run[run.length - 1]])}` : run.map(d => t(DAY_KEYS[d])).join(', ')
    )
    .join(', ');
  const parts = window.ranges.map(([from, to]) => `${from}–${to}`);
  const hours = parts.length > 1 ? `${parts.slice(0, -1).join(', ')} ${t('wizRevAnd')} ${parts[parts.length - 1]}` : parts[0];
  return t('wizRevWorkWindow', { days, hours });
}
